import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import { schema } from 'normalizr';

export interface Quiz {
  id: number;
  version: number;
  createdById: number;
  isSharingEnabled: boolean;
  createdDate: Date;
  lastUpdated: Date;
  name: string;
  socNumber: string;
  questions: Array<string | number>;
  standard: QuizStandard;
}

export interface QuizQuestion {
  type:
    | QuestionTypes.FreeResponse
    | QuestionTypes.MultipleChoice
    | QuestionTypes.TrueFalse;
  questionId: string | number;
  createdById: number;
  createdDate: Date;
  hasCorrectAnswer?: boolean;
  order: number;
  temporaryOrder?: number;
  questionImage?: ImageResource;
  questionText: string;
  explanation: string;
  explanationImage?: ImageResource;
  gradingWeight: number;
  answers: Array<string | number>;
  resources?: ImageResource[];
}

export interface Invalidation {
  description: string;
  title: string;
  meta?: string;
  inputIds?: string[];
}

export interface QuizAnswer {
  id: string | number;
  createdById: number;
  text: string;
  image?: ImageResource;
  isCorrect: boolean;
  order: number;
  questionId: string | number;
  resources: ImageResource[];
}

export interface ImageResource {
  id: string | number;
  url: string;
  type: string;
  urlXS?: string;
  urlS?: string;
  urlM?: string;
  urlL?: string;
  urlXL?: string;
}

export enum ImageResourceSizes {
  XXL = 1024,
  XL = 820,
  L = 600,
  M = 480,
  S = 320,
  XS = 160,
}

export interface QuizStandard {
  id: number;
  subjectId: number;
  coreId: number;
  gradeId: number;
  standardId: number;
  name: string;
  description: string;
  quizId: number;
}

function createTrueFalse({
  questionId,
  order,
  questionType,
}: {
  questionId: string;
  order: number;
  questionType: QuestionTypes;
}) {
  const answers: QuizAnswer[] = [
    {
      questionId: questionId,
      text: 'True',
      createdById: -1,
      id: `editing-${new Date().getTime()}-1`,
      isCorrect: true,
      resources: [],
      order: 1,
    },
    {
      questionId: questionId,
      text: 'False',
      createdById: -1,
      id: `editing-${new Date().getTime()}-2`,
      isCorrect: false,
      resources: [],
      order: 2,
    },
  ];
  const question = {
    answers: answers.map((a) => a.id),
    order,
    createdById: null,
    createdDate: null,
    explanation: '',
    type: questionType,
    resources: [],
    gradingWeight: 1,
    questionId,
    questionText: '',
  };
  return { question, answers };
}

function createMultipleChoice({
  questionId,
  order,
  questionType,
}: {
  questionId: string;
  order: number;
  questionType: QuestionTypes;
}) {
  const answers: QuizAnswer[] = Array.from({ length: 4 }, (value, index) => ({
    questionId: questionId,
    text: '',
    createdById: -1,
    id: `editing-${new Date().getTime()}-${index}`,
    isCorrect: false,
    resources: [],
    order: index + 1,
  }));

  const question = {
    answers: answers.map((a) => a.id),
    order,
    createdById: null,
    createdDate: null,
    explanation: '',
    type: questionType,
    resources: [],
    gradingWeight: 1,
    questionId,
    questionText: '',
  };
  return { question, answers };
}

function createFreeResponseQuestion({
  questionId,
  order,
  questionType,
}: {
  questionId: string;
  order: number;
  questionType: QuestionTypes;
}) {
  const answers: QuizAnswer[] = Array.from({ length: 5 }, (value, index) => ({
    questionId: questionId,
    text: '',
    createdById: -1,
    id: `editing-${new Date().getTime()}-${index}`,
    isCorrect: true,
    resources: [],
    order: index + 1,
  }));
  const question = {
    answers: answers.map((a) => a.id),
    createdById: null,
    order,
    createdDate: null,
    explanation: '',
    type: questionType,
    resources: [],
    gradingWeight: 1,
    questionId,
    questionText: '',
  };
  return { question, answers };
}

// TODO: Write unit tests...
export const createQuestion = ({
  questionType,
  order,
  questionId,
}: {
  questionType: QuestionTypes;
  order: number;
  questionId: string;
}): { question: QuizQuestion; answers: QuizAnswer[] } => {
  switch (questionType) {
    case QuestionTypes.FreeResponse: {
      return createFreeResponseQuestion({ questionId, order, questionType });
    }
    case QuestionTypes.MultipleChoice: {
      return createMultipleChoice({ questionId, order, questionType });
    }
    case QuestionTypes.TrueFalse: {
      return createTrueFalse({ questionId, order, questionType });
    }
    default:
      throw new Error('Invalid question type.');
  }
};

export const QuizAnswerSchema = new schema.Entity('quizAnswer');
export const QuizQuestionSchema = new schema.Entity(
  'quizQuestion',
  { answers: [QuizAnswerSchema] },
  {
    idAttribute: 'questionId',
  }
);

export const QuizSchema = new schema.Entity(
  'quiz',
  { questions: [QuizQuestionSchema] },
  { idAttribute: 'id' }
);
